import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageHeader, Toast, Modal } from "@/components/Layout";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || "Eroare";
const KIND_LABEL = { serie: "Serii", grupa_neclinic: "Grupe preclinice (fără stagiu clinic)", grupa_clinic: "Grupe clinice (cu stagiu clinic)",
  grupa_complementar: "Grupe complementare", grupa_seminar: "Grupe de seminar", grupa_aplicativ: "Grupe de activități aplicative" };
const KIND_SHORT = { serie: "serie", grupa_neclinic: "preclinică", grupa_clinic: "clinică", grupa_complementar: "complementară", grupa_seminar: "de seminar", grupa_aplicativ: "aplicativă" };
let KEY = 0;
const mk = (f) => ({ key: ++KEY, id: f.id || null, kind: f.kind, name: f.name, students: f.students, series_name: f.series_name || "" });

export default function CohortEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState("");
  const [dirty, setDirty] = useState(false);
  const [years, setYears] = useState([]);
  const [confirmPropose, setConfirmPropose] = useState(false);
  const [copying, setCopying] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3500); };

  const apply = (d) => { setC(d); setRows(d.formations.map(mk)); setStudents(String(d.students_count)); setDirty(false); };
  const load = () => api.get(`/stat-didactic/cohorts/${id}`).then(r => apply(r.data)).catch(() => setC(false));
  useEffect(() => { load(); api.get("/stat-didactic/years").then(r => setYears(r.data)).catch(() => {}); }, [id]);
  if (c === null) return <div>Se încarcă...</div>;
  if (c === false) return <div>Efectiv inexistent. <Link to="/stat-didactic/formatiuni">Înapoi</Link></div>;

  const upd = (key, patch) => { setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r))); setDirty(true); };
  const remove = (key) => { setRows(rs => rs.filter(r => r.key !== key)); setDirty(true); };
  const series = rows.filter(r => r.kind === "serie");
  const add = (kind) => {
    const n = rows.filter(r => r.kind === kind).length + 1;
    setRows(rs => [...rs, { key: ++KEY, id: null, kind, name: kind === "serie" ? `Seria ${String.fromCharCode(64 + n)}` : `Grupa ${KIND_SHORT[kind]} ${n}`, students: 0, series_name: kind === "serie" ? "" : (series[0]?.name || "") }]);
    setDirty(true);
  };
  const save = async () => {
    try {
      const r = await api.put(`/stat-didactic/cohorts/${id}/formations`, { formations: rows.map(x => ({ id: x.id, kind: x.kind, name: x.name, students: Number(x.students) || 0, series_name: x.series_name || null })) });
      apply(r.data); toast("Formațiuni salvate", "success");
    } catch (e) { toast(errText(e), "error"); }
  };
  const saveStudents = async () => {
    try {
      const r = await api.put("/stat-didactic/cohorts", { program_id: c.program_id, academic_year_id: c.academic_year_id, study_year: c.study_year, students_count: Number(students) || 0 });
      setC(prev => ({ ...prev, ...r.data, formations: prev.formations })); toast("Efectiv salvat", "success"); load();
    } catch (e) { toast(errText(e), "error"); }
  };
  const propose = async () => {
    setConfirmPropose(false);
    try { const r = await api.post(`/stat-didactic/cohorts/${id}/propose`, { replace: true }); apply(r.data); toast("Formațiuni propuse", "success"); }
    catch (e) { toast(errText(e), "error"); }
  };
  const doCopy = async () => {
    try { const r = await api.post(`/stat-didactic/cohorts/${id}/copy`, { to_academic_year_id: copying }); setCopying(null); nav(`/stat-didactic/formatiuni/${r.data.id}`); }
    catch (e) { toast(errText(e), "error"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/stat-didactic/cohorts/${id}`); nav(`/stat-didactic/formatiuni?program=${c.program_id}&year=${c.academic_year_id}`); }
    catch (e) { toast(errText(e), "error"); setDeleting(false); }
  };

  const block = (kind) => {
    const list = rows.filter(r => r.kind === kind);
    const sz = c.sizes[kind];
    return (
      <div className="card" style={{ padding: 16, marginBottom: 12 }} key={kind} data-testid={`sfd-coh-block-${kind}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <b>{KIND_LABEL[kind]}</b>
          {sz && <span style={{ fontSize: 12, color: "#64748B" }}>
            {[sz.min_students != null && `min. ${sz.min_students}`, sz.max_students != null && `max. ${sz.max_students}`, sz.aracis_max != null && `plafon ARACIS ${sz.aracis_max}`].filter(Boolean).join(" · ")}</span>}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5 }}>{list.length} · {list.reduce((s, r) => s + (Number(r.students) || 0), 0)} studenți</span>
          <button className="btn btn-secondary" style={{ padding: "2px 10px", fontSize: 12 }} onClick={() => add(kind)} data-testid={`sfd-coh-add-${kind}`}>+ {kind === "serie" ? "Serie" : "Grupă"}</button>
        </div>
        {list.length > 0 && (
          <table className="tbl" style={{ fontSize: 12.5 }}>
            <thead><tr><th>Denumire</th>{kind !== "serie" && <th>Seria</th>}<th>Studenți</th><th></th></tr></thead>
            <tbody>{list.map(r => (
              <tr key={r.key}>
                <td><input className="input" style={{ padding: "3px 6px" }} value={r.name} onChange={e => upd(r.key, { name: e.target.value })} data-testid={`sfd-coh-name-${r.name}`} /></td>
                {kind !== "serie" && <td><select className="select" style={{ padding: "3px 4px" }} value={r.series_name} onChange={e => upd(r.key, { series_name: e.target.value })}><option value="">—</option>{series.map(s => <option key={s.key} value={s.name}>{s.name}</option>)}</select></td>}
                <td><input className="input" type="number" min="0" style={{ width: 90, padding: "3px 6px" }} value={r.students} onChange={e => upd(r.key, { students: e.target.value })} /></td>
                <td><button className="btn btn-ghost" style={{ padding: "1px 8px", color: "#DC2626" }} onClick={() => remove(r.key)}>✕</button></td>
              </tr>))}</tbody>
          </table>)}
      </div>);
  };

  return (
    <>
      <PageHeader title={`${c.program.name} — anul ${c.study_year}`} subtitle={`Formațiuni de studii ${c.year.name}`}
        actions={<>
          <Link className="btn btn-secondary" to={`/stat-didactic/formatiuni?program=${c.program_id}&year=${c.academic_year_id}`}>← Efective</Link>
          <button className="btn btn-primary" disabled={!dirty} onClick={save} data-testid="sfd-coh-save">Salvează formațiunile</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      <div className="card" style={{ padding: 16, marginBottom: 14, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div><label className="label">Studenți în anul {c.study_year}</label>
          <input className="input" type="number" min="0" style={{ width: 130 }} value={students} onChange={e => setStudents(e.target.value)} data-testid="sfd-coh-count" /></div>
        <button className="btn btn-secondary" onClick={saveStudents} disabled={String(c.students_count) === students} data-testid="sfd-coh-count-save">Salvează efectivul</button>
        <button className="btn btn-primary" onClick={() => (rows.length ? setConfirmPropose(true) : propose())} disabled={c.students_count <= 0} data-testid="sfd-coh-propose">Propune formațiuni</button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-secondary" onClick={() => setCopying(years.find(y => y.id !== c.academic_year_id)?.id || "")}>Copiază în alt an</button>
        <button className="btn btn-ghost" style={{ color: "#DC2626" }} onClick={() => setDeleting(true)}>Șterge efectivul</button>
      </div>
      {dirty && <div className="te-closed" data-testid="sfd-coh-dirty">Ai modificări nesalvate; avertismentele se actualizează după salvare.</div>}
      {block("serie")}
      {c.group_kinds.map(block)}
      {c.warnings.length > 0 && (
        <div className="te-closed" data-testid="sfd-coh-warnings">
          <b>Atenție</b><ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{c.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>)}
      {confirmPropose && (
        <Modal title="Înlocuiești formațiunile?" onClose={() => setConfirmPropose(false)} width={480}>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 16 }}>Seriile și grupele existente (inclusiv denumirile modificate de tine) se înlocuiesc cu o propunere nouă, calculată din efectiv și din dimensiunile anului.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setConfirmPropose(false)}>Anulează</button>
            <button className="btn btn-primary" onClick={propose} data-testid="sfd-coh-propose-confirm">Înlocuiește</button>
          </div>
        </Modal>)}
      {copying !== null && (
        <Modal title="Copiază formațiunile în alt an universitar" onClose={() => setCopying(null)} width={480}>
          <select className="select" value={copying} onChange={e => setCopying(e.target.value)}>{years.filter(y => y.id !== c.academic_year_id).map(y => <option key={y.id} value={y.id}>{y.name}</option>)}</select>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Se copiază efectivul, denumirile și numărul de studenți din fiecare formațiune; ajustezi apoi în anul nou.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setCopying(null)}>Anulează</button>
            <button className="btn btn-primary" disabled={!copying} onClick={doCopy}>Copiază</button>
          </div>
        </Modal>)}
      {deleting && (
        <Modal title="Șterge efectivul și formațiunile lui?" onClose={() => setDeleting(false)} width={460}>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={() => setDeleting(false)}>Anulează</button>
            <button className="btn btn-danger" onClick={doDelete}>Șterge</button>
          </div>
        </Modal>)}
    </>
  );
}
